import { useState } from 'react';
import { Users, Maximize2, Bed, Wifi, Tv, WashingMachine, ChefHat, Snowflake, ChevronLeft, ChevronRight } from 'lucide-react';

const apartments = [
  {
    id: 'riesling',
    name: 'Riesling',
    number: 4,
    tagline: 'Confort et charme alsacien',
    size: '65 m\u00B2',
    capacity: '2 a 5 personnes',
    floor: '1er etage',
    description: 'Appartement spacieux au 1er etage avec sejour ouvert sur la cuisine et salle a manger. Deux chambres confortables, salle de bains et tout l\'equipement necessaire pour un sejour parfait.',
    bedrooms: [
      '1ere chambre : 1 lit double 140x190 + 1 lit simple 90x190',
      '2eme chambre : 1 lit double 140x190'
    ],
    price_low: '469',
    price_high: '549',
    amenities: ['WiFi gratuit', 'Cuisine equipee', 'Four & micro-ondes', 'TV satellite 71cm', 'Home cinema DVD', 'Lave-linge', 'Fer a repasser', 'Mini-bar'],
    images: [
      'https://images.unsplash.com/photo-1698870157085-11632d2ddef8?q=80&w=1200&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1566152474719-8d79ca1a4c66?q=80&w=1200&auto=format&fit=crop',
    ]
  },
  {
    id: 'muscat',
    name: 'Muscat',
    number: 2,
    tagline: 'Duplex d\'exception',
    size: '75 m\u00B2',
    capacity: '2 a 6 personnes',
    floor: '2eme et 3eme etages',
    description: 'Magnifique duplex sur deux niveaux offrant un espace genereux. Cuisine equipee avec lave-vaisselle, sejour lumineux, deux chambres et salle de bain. Une experience unique dans une maison historique.',
    bedrooms: [
      '1ere chambre : 1 lit double 140x190',
      '2eme chambre : 1 lit double 140x190',
      'Salon : 1 canape convertible 2 places + 2 canapes 1 place'
    ],
    price_low: '519',
    price_high: '599',
    amenities: ['WiFi gratuit', 'Cuisine avec lave-vaisselle', 'Four & micro-ondes', 'TV satellite 71cm', 'Chaine HiFi', 'Lave-linge', 'Aspirateur', 'Duplex'],
    images: [
      'https://images.unsplash.com/photo-1731060458043-14bad97ef745?q=80&w=1200&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1757620057455-d91859d43a8b?q=80&w=1200&auto=format&fit=crop',
    ]
  }
];

const iconMap = {
  'WiFi gratuit': Wifi,
  'TV satellite 71cm': Tv,
  'Lave-linge': WashingMachine,
  'Cuisine equipee': ChefHat,
  'Cuisine avec lave-vaisselle': ChefHat,
  'Mini-bar': Snowflake,
};

function ApartmentCard({ apt }) {
  const [imgIndex, setImgIndex] = useState(0);

  const nextImg = () => setImgIndex((i) => (i + 1) % apt.images.length);
  const prevImg = () => setImgIndex((i) => (i - 1 + apt.images.length) % apt.images.length);

  return (
    <div
      data-testid={`apartment-card-${apt.id}`}
      className="bg-white border border-cream-400 overflow-hidden group"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={apt.images[imgIndex]}
          alt={`Appartement ${apt.name}`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />

        {apt.images.length > 1 && (
          <>
            <button
              onClick={prevImg}
              data-testid={`${apt.id}-prev-img`}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={nextImg}
              data-testid={`${apt.id}-next-img`}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}

        <div className="absolute top-4 left-4">
          <span className="bg-wine-700 text-white px-4 py-1.5 font-body text-xs uppercase tracking-[0.15em]">
            N&deg;{apt.number}
          </span>
        </div>

        <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
          <div>
            <h3 className="font-display text-3xl text-white font-semibold">{apt.name}</h3>
            <p className="font-accent text-white/80 text-lg italic">{apt.tagline}</p>
          </div>
        </div>
      </div>

      <div className="p-8 md:p-10">
        <div className="flex flex-wrap gap-6 mb-8">
          <div className="flex items-center gap-2 text-earth-200">
            <Maximize2 size={16} className="text-wine-700" />
            <span className="font-body text-sm">{apt.size}</span>
          </div>
          <div className="flex items-center gap-2 text-earth-200">
            <Users size={16} className="text-wine-700" />
            <span className="font-body text-sm">{apt.capacity}</span>
          </div>
          <div className="flex items-center gap-2 text-earth-200">
            <Bed size={16} className="text-wine-700" />
            <span className="font-body text-sm">{apt.floor}</span>
          </div>
        </div>

        <p className="font-body text-earth-200 leading-relaxed mb-8">
          {apt.description}
        </p>

        <div className="mb-8">
          <h4 className="font-display text-lg text-earth-100 mb-3">Couchages</h4>
          <ul className="space-y-1.5">
            {apt.bedrooms.map((bed, i) => (
              <li key={i} className="font-body text-sm text-earth-200 flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-wine-700 rounded-full mt-1.5 flex-shrink-0" />
                {bed}
              </li>
            ))}
          </ul>
        </div>

        <div className="mb-10">
          <h4 className="font-display text-lg text-earth-100 mb-3">Equipements</h4>
          <div className="flex flex-wrap gap-2">
            {apt.amenities.map((amenity) => (
              <span
                key={amenity}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cream-50 border border-cream-400 font-body text-xs text-earth-200"
              >
                {amenity}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-end justify-between pt-8 border-t border-cream-400">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.15em] text-earth-300 mb-1">A partir de</p>
            <p className="font-display text-3xl text-wine-700">{apt.price_low}&euro;</p>
            <p className="font-body text-sm text-earth-300">la semaine (basse saison)</p>
          </div>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid={`reserve-${apt.id}-btn`}
            className="px-8 py-3.5 bg-wine-700 text-white font-body text-xs uppercase tracking-[0.2em] hover:bg-wine-800 transition-all duration-300 shadow-md hover:shadow-lg"
          >
            Reserver
          </a>
        </div>
      </div>
    </div>
  );
}

export default function ApartmentsSection() {
  return (
    <section
      id="appartements"
      data-testid="apartments-section"
      className="py-24 md:py-32 bg-cream-50 relative grain-overlay"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="text-center mb-16 md:mb-20">
          <p className="font-accent text-wine-700 text-lg tracking-[0.2em] uppercase mb-4">
            Nos logements
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-earth-100 mb-6">
            Appartements de charme
          </h2>
          <p className="font-body text-earth-200 text-lg max-w-2xl mx-auto leading-relaxed">
            Deux appartements soigneusement renoves dans des maisons a colombages du XVIe siecle,
            au coeur de la Cour Renaissance de Riquewihr
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {apartments.map((apt) => (
            <ApartmentCard key={apt.id} apt={apt} />
          ))}
        </div>
      </div>
    </section>
  );
}
